import {
  IonApp,
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route } from "react-router-dom";
import {
  bagOutline,
  chevronForward,
  logInOutline,
  personOutline,
  videocamOutline,
} from "ionicons/icons";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Storage } from "@capacitor/storage";
import { useCallback, useEffect, useState } from "react";
import { Provider } from "mobx-react";
import axios from "axios";

import Login from "./pages/Login";
import Home from "./pages/Home";
import MagazinePage from "./pages/MagazinePage";
import MagazineDetail from "./pages/MagazineDetail";
import EmployeeInformation from "./pages/EmployeeInformation";
import Profile from "./pages/Profile";
import PasswordForgot from "./pages/PasswordForgot";
import ProductsPage from "./pages/ProductsPage";
import AboutPage from "./pages/AboutPage";
import Register from "./pages/Register";
import Mediathek from "./pages/Mediathek";
import { firebaseMessagingVapidKey } from "./firebase";

import config from "./config/main.config";
import mainPackage from "../package.json";
import history from "./history";
import {
  getTokenFromLocalStorage,
  getUserFromLocalStorage,
  infosToUnreadCount,
  removeTokenFromStorage,
} from "./helper/utils";

import "@ionic/react/css/core.css";
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";
import "./theme/variables.css";
import "./theme/text.css";

// Only import PushNotifications types - actual usage will be guarded
import type {
  PushNotification,
  PushNotificationActionPerformed,
  Token,
} from "@capacitor/push-notifications";

type AppBadgePlugin = {
  setBadgeCount(options: { count: number }): Promise<void>;
  clearBadge(): Promise<void>;
};

type WebMessaging = {
  getToken(options: {
    vapidKey: string;
    serviceWorkerRegistration: ServiceWorkerRegistration;
  }): Promise<string | null>;
};

type FirebaseWithMessaging = {
  messaging: (() => WebMessaging) & {
    isSupported?: () => boolean;
  };
};

const AppBadge = registerPlugin<AppBadgePlugin>("AppBadge");

const api = axios.create({ baseURL: config.BASE_URL });

// Helper to save/load last token + timestamp
const TOKEN_KEY = "last_fcm_token";
const LAST_REFRESH_KEY = "last_token_refresh";
const WEB_TOKEN_KEY = "last_web_fcm_token";
const WEB_LAST_REFRESH_KEY = "last_web_token_refresh";
const TOKEN_REFRESH_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

async function saveToken(token: string) {
  await Storage.set({ key: TOKEN_KEY, value: token });
}

async function saveWebToken(token: string) {
  await Storage.set({ key: WEB_TOKEN_KEY, value: token });
}

async function getSavedToken(): Promise<string | null> {
  const { value } = await Storage.get({ key: TOKEN_KEY });
  return value;
}

async function getSavedWebToken(): Promise<string | null> {
  const { value } = await Storage.get({ key: WEB_TOKEN_KEY });
  return value;
}

async function saveRefreshTimestamp(key = LAST_REFRESH_KEY) {
  await Storage.set({ key, value: Date.now().toString() });
}

async function getLastRefreshTimestamp(key = LAST_REFRESH_KEY): Promise<number> {
  const { value } = await Storage.get({ key });
  return value ? parseInt(value, 10) : 0;
}

function shouldSyncToken(
  token: string,
  savedToken: string | null,
  lastRefreshTimestamp: number,
): boolean {
  if (token !== savedToken) return true;
  if (!lastRefreshTimestamp) return true;
  return Date.now() - lastRefreshTimestamp >= TOKEN_REFRESH_INTERVAL_MS;
}

async function syncAppBadge(count: number) {
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;

  if (typeof navigator !== "undefined") {
    const badgeNavigator = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };

    try {
      if (safeCount > 0 && typeof badgeNavigator.setAppBadge === "function") {
        await badgeNavigator.setAppBadge(safeCount);
      } else if (typeof badgeNavigator.clearAppBadge === "function") {
        await badgeNavigator.clearAppBadge();
      }
    } catch (err) {
      console.warn("PWA badge update failed:", err);
    }
  }

  if (Capacitor.getPlatform() === "ios") {
    try {
      if (safeCount > 0) {
        await AppBadge.setBadgeCount({ count: safeCount });
      } else {
        await AppBadge.clearBadge();
      }
    } catch (err) {
      console.warn("iOS badge update failed:", err);
    }
  }
}

// ────────────────────────────────────────────────
// Token Management
// ────────────────────────────────────────────────
async function sendTokenToServer(
  token: string,
  authToken: string,
): Promise<void> {
  try {
    await api.post(
      "/pns",
      { pushNotificationToken: token },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
    console.log("Push token sent to server successfully");
  } catch (err) {
    console.error("Failed to send push token to server:", err);
  }
}

//tab icons
const homeIcon = "/assets/buttons/home.svg";
const informationIcon = "/assets/buttons/info.svg";
const magazineIcon = "/assets/buttons/magazine.svg";

interface AppPage {
  url: string;
  tab: string;
  iosIcon: string;
  mdIcon: string;
  title: string;
}

const appPages: AppPage[] = [
  {
    title: "Produkte",
    url: "/products",
    tab: "products",
    iosIcon: bagOutline,
    mdIcon: bagOutline,
  },
  {
    title: "Magazin",
    url: "/magazine",
    tab: "magazine",
    iosIcon: magazineIcon,
    mdIcon: magazineIcon,
  },
  {
    title: "Home",
    url: "/home",
    tab: "home",
    iosIcon: homeIcon,
    mdIcon: homeIcon,
  },
];

const appToolbar: AppPage[] = [
  {
    title: "Login",
    url: "/login",
    tab: "login",
    iosIcon: logInOutline,
    mdIcon: logInOutline,
  },
];

const loggedInPages: AppPage[] = [
  {
    title: "Produkte",
    url: "/products",
    tab: "products",
    iosIcon: bagOutline,
    mdIcon: bagOutline,
  },
  {
    title: "Magazin",
    url: "/magazine",
    tab: "magazine",
    iosIcon: magazineIcon,
    mdIcon: magazineIcon,
  },
  {
    title: "Home",
    url: "/home",
    tab: "home",
    iosIcon: homeIcon,
    mdIcon: homeIcon,
  },
  {
    title: "Information",
    url: "/information",
    tab: "information",
    iosIcon: informationIcon,
    mdIcon: informationIcon,
  },
  {
    title: "Media",
    url: "/mediathek",
    tab: "mediathek",
    iosIcon: videocamOutline,
    mdIcon: videocamOutline,
  },
];

const loggedInToolbar: AppPage[] = [
  {
    title: "Profil",
    url: "/profile",
    tab: "profile",
    iosIcon: personOutline,
    mdIcon: personOutline,
  },
];

const DEFAULT_USER = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  username: "",
};

const App: React.FC = () => {
  const [user, setUser] = useState(DEFAULT_USER);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [importantInfo, setImportantInfo] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [magazines, setMagazines] = useState<any[]>([]);
  const [media, setMedia] = useState<any[]>([]);
  const [info, setInfo] = useState<any[]>([]);
  const [newestArticles, setNewestArticles] = useState<any[]>([]);

  const isNativePlatform = Capacitor.isNativePlatform();

  const getAuthHeaders = useCallback(async () => {
    const token = await getTokenFromLocalStorage();
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const getAllMagazines = useCallback(async () => {
    try {
      const res = await api.get("magazine-article/all");
      const returnMagazines = res.data;
      setMagazines(returnMagazines);

      if (returnMagazines?.length > 0) {
        const newest = returnMagazines[0];
        setNewestArticles(
          newest.articles?.length > 5
            ? newest.articles.slice(0, 5)
            : newest.articles || [],
        );
      }
    } catch (err) {
      console.error("Failed to load magazines:", err);
    }
  }, []);

  const getUserInformation = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await api.get("informations/user_all", { headers });
      const infos = res.data.infos || [];
      setInfo(infos);

      const unread = infosToUnreadCount(infos);
      setUnreadMessages(unread);

      const hasImportantUnread = infos.some(
        (i: any) => i.important === 1 && !i.read,
      );
      setImportantInfo(hasImportantUnread);
    } catch (err) {
      console.error("Failed to load user info:", err);
    }
  }, [getAuthHeaders]);

  const getMedia = useCallback(async () => {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      const res = await api.get("mediathek/all", { headers });
      setMedia(res.data.infos || []);
    } catch (err) {
      console.error("Failed to load media:", err);
    }
  }, [getAuthHeaders]);

  const refreshPushToken = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return; // Skip on web

    try {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      const permResult = await PushNotifications.requestPermissions();
      if (permResult.receive === "granted") {
        await PushNotifications.register();
      }
    } catch (err) {
      console.warn("Push token refresh failed:", err);
    }
  }, []);

  const refreshWebPushToken = useCallback(async () => {
    if (Capacitor.isNativePlatform()) return;
    if (typeof window === "undefined") return;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

    try {
      const permission =
        Notification.permission === "granted"
          ? "granted"
          : await Notification.requestPermission();

      if (permission !== "granted") return;

      const firebaseModule = await import("./firebase");
      const appFirebase = firebaseModule.default as unknown as FirebaseWithMessaging;

      if (
        typeof appFirebase.messaging !== "function" ||
        typeof appFirebase.messaging.isSupported !== "function" ||
        !appFirebase.messaging.isSupported()
      ) {
        return;
      }

      const serviceWorkerRegistration = await navigator.serviceWorker.ready;
      const messaging = appFirebase.messaging();
      const token = await messaging.getToken({
        vapidKey: firebaseMessagingVapidKey,
        serviceWorkerRegistration,
      });

      if (!token) return;

      const authToken = await getTokenFromLocalStorage();
      if (!authToken) return;

      const savedToken = await getSavedWebToken();
      const lastRefreshTimestamp = await getLastRefreshTimestamp(WEB_LAST_REFRESH_KEY);

      if (shouldSyncToken(token, savedToken, lastRefreshTimestamp)) {
        await sendTokenToServer(token, authToken);
        await saveWebToken(token);
        await saveRefreshTimestamp(WEB_LAST_REFRESH_KEY);
      }
    } catch (err) {
      console.warn("Web push token refresh failed:", err);
    }
  }, []);

  const registerToken = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      await refreshPushToken();
      return;
    }

    await refreshWebPushToken();
  }, [refreshPushToken, refreshWebPushToken]);

  useEffect(() => {
    void syncAppBadge(unreadMessages);
  }, [unreadMessages]);

  // ────────────────────────────────────────────────
  // Global Push Listeners + Initial Load
  // ────────────────────────────────────────────────
  useEffect(() => {
    // Load user & data
    getUserFromLocalStorage().then((storedUser) => {
      if (storedUser?.id) {
        setUser(storedUser);
      }
    });

    getUserInformation();
    getAllMagazines();
    getMedia();

    // Only setup push listeners on native
    if (isNativePlatform) {
      void (async () => {
        const { PushNotifications } = await import("@capacitor/push-notifications");

        await PushNotifications.addListener("registration", async (token: Token) => {
          const authToken = await getTokenFromLocalStorage();
          if (!authToken) return;

          const savedToken = await getSavedToken();
          const lastRefreshTimestamp = await getLastRefreshTimestamp();

          if (shouldSyncToken(token.value, savedToken, lastRefreshTimestamp)) {
            await sendTokenToServer(token.value, authToken);
            await saveToken(token.value);
            await saveRefreshTimestamp();
          }
        });

        await PushNotifications.addListener("registrationError", (error: any) => {
          console.error("Push registration error:", error);
        });

        await PushNotifications.addListener(
          "pushNotificationReceived",
          (notification: PushNotification) => {
            console.log("Foreground notification received:", notification);

            if (typeof notification.badge === "number") {
              setUnreadMessages(Math.max(0, notification.badge));
            }

            void getUserInformation();
          },
        );

        await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          (action: PushNotificationActionPerformed) => {
            console.log("Notification action performed:", action);
            void getUserInformation();
          },
        );

        // Register only after listeners are attached to avoid missing the first token event.
        await refreshPushToken();
      })();
    } else {
      void refreshWebPushToken();
    }

    return () => {
      if (isNativePlatform) {
        import("@capacitor/push-notifications").then(({ PushNotifications }) => {
          PushNotifications.removeAllListeners();
        });
      }
    };
  }, [
    getAllMagazines,
    getMedia,
    getUserInformation,
    isNativePlatform,
    refreshPushToken,
    refreshWebPushToken,
  ]);

  async function handleLogout() {
    try {
      const token = await getTokenFromLocalStorage();
      if (token) {
        try {
          await api.delete(`/pns/${token}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          console.log("Server token deleted");
        } catch {}
      }

      await removeTokenFromStorage();
      await Storage.remove({ key: TOKEN_KEY });
      await Storage.remove({ key: LAST_REFRESH_KEY });
      await Storage.remove({ key: WEB_TOKEN_KEY });
      await Storage.remove({ key: WEB_LAST_REFRESH_KEY });
      await syncAppBadge(0);
    } catch {}

    window.location.reload();
  }

  async function setRead(id: string) {
    const headers = await getAuthHeaders();
    if (!headers) return;

    try {
      await api.put("informations/seen", { id }, { headers });
      history.push("/information");
      await getUserInformation();
    } catch {}
  }

  return (
    <IonApp>
      <Provider>
        <IonHeader>
          {importantInfo && (
            <IonToolbar
              color="danger"
              onClick={() => history.push("/information")}
            >
              <IonTitle>Wichtige Information</IonTitle>
              <IonButtons slot="end">
                <IonButton>
                  <IonIcon icon={chevronForward} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          )}
          <IonToolbar>
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "5px",
              }}
            >
              <span style={{ color: "lightgray", fontSize: "7pt" }}>
                {"v " + mainPackage.version}
              </span>
            </div>
            <IonButtons slot="end">
              {(user.id !== "" ? loggedInToolbar : appToolbar).map(
                (appPage, index) => {
                  return (
                    <IonButton key={appPage.tab} href={appPage.url}>
                      <IonIcon ios={appPage.iosIcon} md={appPage.mdIcon} />
                      {appPage.tab === "information" && unreadMessages > 0 ? (
                        <IonBadge>{unreadMessages}</IonBadge>
                      ) : null}
                    </IonButton>
                  );
                },
              )}
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          <IonReactRouter history={history}>
            <IonTabs>
              <IonRouterOutlet>
                <Route path="/home" exact>
                  <Home
                    articles={newestArticles}
                    info={info}
                    importantInfo={importantInfo}
                  />
                </Route>
                <Route path="/products" exact>
                  <ProductsPage importantInfo={importantInfo} user={user} />
                </Route>
                <Route path="/magazine" exact>
                  <MagazinePage
                    magazines={magazines}
                    importantInfo={importantInfo}
                  />
                </Route>
                <Route path="/magazine-detail/:id" exact>
                  <MagazineDetail importantInfo={importantInfo} />
                </Route>
                <Route path="/information" exact>
                  <EmployeeInformation
                    setUnreadMessages={setUnreadMessages}
                    info={info}
                    importantInfo={importantInfo}
                    setRead={setRead}
                    refreshArticles={getUserInformation}
                  />
                </Route>
                <Route path="/mediathek" exact>
                  <Mediathek
                    media={media}
                    importantInfo={importantInfo}
                    refreshArticles={getMedia}
                  />
                </Route>
                <Route path="/login" exact>
                  <Login setUser={setUser} registerToken={registerToken} />
                </Route>
                <Route path="/profile" exact>
                  <Profile
                    user={user}
                    logout={handleLogout}
                    importantInfo={importantInfo}
                  />
                </Route>
                <Route path="/register" exact component={Register} />
                <Route
                  path="/password-forgot"
                  exact
                  component={PasswordForgot}
                />
                <Route path="/about" exact>
                  <AboutPage importantInfo={importantInfo} />
                </Route>
                <Route exact path="/">
                  <Redirect to="/home" />
                </Route>
              </IonRouterOutlet>
              <IonTabBar slot="bottom">
                {(user.id ? loggedInPages : appPages).map((page) => (
                  <IonTabButton key={page.tab} tab={page.tab} href={page.url}>
                    <IonIcon ios={page.iosIcon} md={page.mdIcon} />
                    <IonLabel>{page.title}</IonLabel>
                    {page.tab === "information" && unreadMessages > 0 && (
                      <IonBadge>{unreadMessages}</IonBadge>
                    )}
                  </IonTabButton>
                ))}
              </IonTabBar>
            </IonTabs>
          </IonReactRouter>
        </IonContent>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
        />
      </Provider>
    </IonApp>
  );
};

export default App;
